import type { EChartsOption } from 'echarts'
import { axisTextStyle, deltaColor, formatCompactNumber, formatSignedShare, isFiniteNumber, relativeDelta, tooltipBase, tooltipHtml, type TooltipRow } from './format'
import { seriesColor, withAlpha, type ChartTheme } from './palette'

export type TimeSeriesKind = 'line' | 'area' | 'bar'

export interface TimeSeriesSeries {
  id: string
  name: string
  /** Значения по датам (`null` — нет данных: линия разрывается, а не падает в ноль). */
  values: Array<number | null>
  /** Ряд сравнения той же сущности (прошлый период / год назад): тот же цвет, пунктир, 50 %. */
  compareValues?: Array<number | null> | null
  compareName?: string
  /** Слот категориальной палитры (цвет закреплён за сущностью); по умолчанию — по порядку рядов. */
  slot?: number
  /** Явный цвет (например, цвет площадки из `marketplaceColor`). */
  color?: string
  /** Рост — плохо (возвраты, отмены): Δ в подсказке красится наоборот. */
  invertDelta?: boolean
  /** Percentage-point change for fractional rates; relative change remains the default. */
  deltaMode?: 'relative' | 'points'
}

export interface TimeSeriesEvent {
  /** Дата из `dates`, к которой относится событие. */
  date: string
  label: string
  type?: string
}

export interface TimeSeriesInput {
  /** Подписи оси X по порядку (обычно `YYYY-MM-DD` или неделя). */
  dates: string[]
  series: TimeSeriesSeries[]
  kind?: TimeSeriesKind
  events?: TimeSeriesEvent[]
  /** Полное значение для подсказки («12 345 ₽»). */
  valueFormat?: (value: number) => string
  /** Короткая подпись оси Y («12 тыс.»). */
  axisFormat?: (value: number) => string
  /** Подпись даты на оси. */
  dateFormat?: (date: string) => string
  /** Заголовок подсказки для точки (полная дата, неделя). */
  tooltipDateFormat?: (date: string, index: number) => string
  /** Ползунок масштаба; по умолчанию включается, когда точек больше 60. */
  zoom?: boolean
  /** Ось Y от нуля (по умолчанию — да; для столбцов и областей всегда). */
  fromZero?: boolean
  /** Подписи событий в подсказке: тип → человекочитаемое название. */
  eventTypeLabels?: Record<string, string>
  /**
   * Типы событий, которые рисуются не вертикальной линией, а узкой полосой у оси X по своим дням (акции:
   * их много, вертикали забивают график). Пара `*_start`/`*_end` с одной подписью — одна полоса от начала до
   * конца. Подсказка по дню показывает событие как прежде.
   */
  bandEventTypes?: string[]
}

export interface EventBand {
  from: number
  to: number
}

/**
 * Полосы событий: пары «начало … конец» с одной подписью — диапазон, остальные — свой день; пересекающиеся и
 * соседние полосы сливаются. Одиночный день растягивается на соседнюю точку, иначе у линейной оси полоса нулевой
 * ширины.
 */
export function eventBands(dates: string[], events: TimeSeriesEvent[] = [], bandTypes: readonly string[] = []): EventBand[] {
  if (bandTypes.length === 0 || dates.length === 0) return []
  const items = eventIndexes(dates, events.filter((event) => bandTypes.includes(event.type ?? ''))).sort((a, b) => a.index - b.index)
  const ranges: EventBand[] = []
  const open = new Map<string, number>()
  for (const { index, event } of items) {
    const type = event.type ?? ''
    if (type.endsWith('_start')) {
      open.set(`${type.slice(0, -6)}|${event.label}`, index)
      continue
    }
    if (type.endsWith('_end')) {
      const key = `${type.slice(0, -4)}|${event.label}`
      const start = open.get(key)
      open.delete(key)
      ranges.push({ from: start ?? index, to: index })
      continue
    }
    ranges.push({ from: index, to: index })
  }
  // Начало без конца в пределах оси — акция идёт до конца периода.
  for (const start of open.values()) ranges.push({ from: start, to: dates.length - 1 })
  const last = dates.length - 1
  const widened = ranges.map((range) =>
    range.from === range.to ? (range.to < last ? { from: range.from, to: range.to + 1 } : { from: Math.max(0, range.from - 1), to: range.to }) : range,
  )
  widened.sort((a, b) => a.from - b.from)
  const merged: EventBand[] = []
  for (const range of widened) {
    const previous = merged[merged.length - 1]
    if (previous && range.from <= previous.to) previous.to = Math.max(previous.to, range.to)
    else merged.push({ ...range })
  }
  return merged
}

/** Высота полосы событий в единицах оси Y: 4 % от максимума рядов (ось Y от нуля). */
export const EVENT_BAND_SHARE = 0.04

export const ZOOM_AUTO_THRESHOLD = 60

/** Номер категории для каждой даты события; события вне оси отбрасываются. */
export function eventIndexes(dates: string[], events: TimeSeriesEvent[] = []): Array<{ index: number; event: TimeSeriesEvent }> {
  const position = new Map(dates.map((date, index) => [date, index]))
  return events
    .map((event) => ({ index: position.get(event.date) ?? -1, event }))
    .filter((item) => item.index >= 0)
}

/**
 * Опции ECharts для ряда по дням/неделям: линии (2 px), область (заливка 10 %) или столбцы (≤ 24 px,
 * скругление 4 px на конце), ряд сравнения пунктиром тем же цветом с прозрачностью 50 %, перекрестие и общая
 * подсказка «все ряды в точке» с Δ, вертикальные отметки событий, ползунок масштаба. Одна ось Y — всегда.
 */
export function buildTimeSeriesOption(input: TimeSeriesInput, theme: ChartTheme): EChartsOption {
  const kind = input.kind ?? 'line'
  const valueFormat = input.valueFormat ?? ((value: number) => formatCompactNumber(value))
  const axisFormat = input.axisFormat ?? ((value: number) => formatCompactNumber(value))
  const dateFormat = input.dateFormat ?? ((date: string) => date)
  const tooltipDate = input.tooltipDateFormat ?? ((date: string) => date)
  const zoom = input.zoom ?? input.dates.length > ZOOM_AUTO_THRESHOLD
  const bandTypes = input.bandEventTypes ?? []
  const events = eventIndexes(input.dates, input.events)
  const lineEvents = events.filter(({ event }) => !bandTypes.includes(event.type ?? ''))
  const bands = eventBands(input.dates, input.events, bandTypes)
  const eventsByIndex = new Map<number, TimeSeriesEvent[]>()
  for (const { index, event } of events) eventsByIndex.set(index, [...(eventsByIndex.get(index) ?? []), event])

  const colors = input.series.map((series, index) => series.color ?? seriesColor(theme, series.slot ?? index))
  const showSymbols = input.dates.length <= 31

  const series: any[] = []
  input.series.forEach((item, index) => {
    const color = colors[index]
    if (item.compareValues && item.compareValues.some((value) => value !== null && value !== undefined)) {
      series.push({
        id: `${item.id}__compare`,
        name: item.compareName ?? `${item.name} — сравнение`,
        type: 'line',
        data: item.compareValues.map((value) => (value === null || value === undefined ? '-' : value)),
        connectNulls: false,
        symbol: 'none',
        lineStyle: { width: 2, type: 'dashed', color, opacity: 0.5 },
        itemStyle: { color: withAlpha(color, 0.5) },
        emphasis: { disabled: true },
        z: 1,
        silent: true,
      })
    }
    const base: any = {
      id: item.id,
      name: item.name,
      data: item.values.map((value) => (value === null || value === undefined ? '-' : value)),
      itemStyle: { color },
      z: 2,
    }
    if (kind === 'bar') {
      series.push({
        ...base,
        type: 'bar',
        barMaxWidth: 24,
        itemStyle: { color, borderRadius: [4, 4, 0, 0] },
        emphasis: { itemStyle: { color, opacity: 0.85 } },
      })
    } else {
      series.push({
        ...base,
        type: 'line',
        connectNulls: false,
        symbol: 'circle',
        symbolSize: 8,
        showSymbol: showSymbols,
        lineStyle: { width: 2, color, cap: 'round', join: 'round' },
        // Кольцо цвета поверхности вокруг точки: читается поверх других линий.
        itemStyle: { color, borderColor: theme.surface, borderWidth: 2 },
        areaStyle: kind === 'area' ? { color: withAlpha(color, 0.1) } : undefined,
        emphasis: { focus: 'none', scale: 1.25 },
      })
    }
  })

  // Отметки событий — на первом основном ряду (ряд сравнения — `silent`, подсказку не даёт).
  const firstMain = series.find((item) => !String(item.id).endsWith('__compare'))
  if (lineEvents.length > 0 && firstMain) {
    firstMain.markLine = {
      silent: true,
      symbol: ['none', 'circle'],
      symbolSize: 6,
      label: { show: false },
      lineStyle: { color: theme.textMuted, width: 1, type: 'solid', opacity: 0.6 },
      data: lineEvents.map(({ index }) => ({ xAxis: index })),
      animation: false,
    }
  }
  // Полосы событий — узкая серая лента у оси X (ось Y от нуля), не перекрывает линии.
  if (bands.length > 0 && firstMain) {
    let max = 0
    for (const item of input.series) {
      for (const value of [...item.values, ...(item.compareValues ?? [])]) {
        if (typeof value === 'number' && Number.isFinite(value)) max = Math.max(max, Math.abs(value))
      }
    }
    if (max > 0) {
      firstMain.markArea = {
        silent: true,
        animation: false,
        itemStyle: { color: withAlpha(theme.textMuted, 0.35) },
        data: bands.map((band) => [
          { xAxis: band.from, yAxis: 0 },
          { xAxis: band.to, yAxis: max * EVENT_BAND_SHARE },
        ]),
      }
    }
  }

  const visibleSeriesCount = series.length
  const legend =
    visibleSeriesCount >= 2
      ? {
          top: 0,
          left: 0,
          itemWidth: 14,
          itemHeight: 8,
          itemGap: 16,
          icon: kind === 'bar' ? 'roundRect' : undefined,
          textStyle: { color: theme.textSecondary, fontFamily: theme.fontFamily, fontSize: 12 },
          inactiveColor: theme.axis,
        }
      : { show: false }

  return {
    backgroundColor: 'transparent',
    animationDuration: 300,
    textStyle: { fontFamily: theme.fontFamily },
    aria: { enabled: true },
    legend,
    grid: {
      top: visibleSeriesCount >= 2 ? 36 : 12,
      left: 8,
      right: 16,
      bottom: zoom ? 56 : 8,
      outerBoundsMode: 'same',
      outerBoundsContain: 'axisLabel',
    } as any,
    tooltip: {
      ...tooltipBase(theme),
      trigger: 'axis',
      axisPointer: {
        type: kind === 'bar' ? 'shadow' : 'line',
        lineStyle: { color: theme.textMuted, width: 1, type: 'solid' },
        shadowStyle: { color: withAlpha(theme.textMuted, 0.08) },
        label: { show: false },
      },
      formatter: (raw: any) => {
        const params = Array.isArray(raw) ? raw : [raw]
        const index = params[0]?.dataIndex ?? 0
        const date = input.dates[index] ?? ''
        const rows: TooltipRow[] = []
        input.series.forEach((item, seriesIndex) => {
          const value = item.values[index] ?? null
          const previous = item.compareValues ? (item.compareValues[index] ?? null) : null
          const delta = item.deltaMode === 'points'
            ? (isFiniteNumber(value) && isFiniteNumber(previous) ? value - previous : null)
            : relativeDelta(value, previous)
          rows.push({
            color: colors[seriesIndex],
            label: item.name,
            value: value === null ? '—' : valueFormat(value),
            note: item.compareValues ? (delta === null ? undefined : item.deltaMode === 'points' ? formatSignedShare(delta).replace('%', ' п.п.') : formatSignedShare(delta)) : undefined,
            noteColor: deltaColor(theme, delta, item.invertDelta),
          })
          if (item.compareValues) {
            rows.push({
              color: colors[seriesIndex],
              dashed: true,
              label: item.compareName ?? 'Сравнение',
              value: previous === null ? '—' : valueFormat(previous),
            })
          }
        })
        const notes = (eventsByIndex.get(index) ?? []).map((event) => {
          const type = event.type ? (input.eventTypeLabels?.[event.type] ?? event.type) : ''
          return type ? `${type}: ${event.label}` : event.label
        })
        return tooltipHtml(theme, tooltipDate(date, index), rows, notes)
      },
    },
    xAxis: {
      type: 'category',
      data: input.dates,
      boundaryGap: kind === 'bar',
      axisLine: { lineStyle: { color: theme.axis } },
      axisTick: { show: false },
      axisLabel: { ...axisTextStyle(theme), formatter: (value: string) => dateFormat(value), hideOverlap: true },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      scale: input.fromZero === false && kind === 'line',
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { ...axisTextStyle(theme), formatter: (value: number) => axisFormat(value) },
      splitLine: { lineStyle: { color: theme.grid, width: 1, type: 'solid' } },
      splitNumber: 4,
    },
    dataZoom: zoom
      ? [
          {
            type: 'slider',
            xAxisIndex: 0,
            bottom: 8,
            height: 22,
            borderColor: theme.grid,
            backgroundColor: 'transparent',
            fillerColor: withAlpha(colors[0] ?? theme.categorical[0], 0.12),
            handleStyle: { color: theme.surface, borderColor: theme.textMuted },
            moveHandleStyle: { color: theme.axis },
            dataBackground: {
              lineStyle: { color: theme.axis, width: 1 },
              areaStyle: { color: withAlpha(theme.textMuted, 0.08) },
            },
            selectedDataBackground: {
              lineStyle: { color: colors[0] ?? theme.categorical[0], width: 1 },
              areaStyle: { color: withAlpha(colors[0] ?? theme.categorical[0], 0.1) },
            },
            textStyle: axisTextStyle(theme),
            labelFormatter: (_value: number, valueStr: string) => dateFormat(valueStr),
            brushSelect: false,
          },
        ]
      : [],
    series,
  } as EChartsOption
}
